import React, { FC, MouseEvent, useMemo } from "react";
import loans from "assets/images/v2/house-icon.svg";
import settings from "assets/images/v2/gear-icon.svg";
import logout from "assets/images/v2/arrow-left-to-bracket.svg";
import "./ProfileMenuDropdown.scss";
import {
  openProfile,
  setLogout,
  getCurrentUserId,
  getDisplayMyLoans,
  getMyLoansFetchedAt,
  getMyLoansStatus,
} from "features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { deleteAllFiles } from "utilities/files";
import { useGetMyLoansQuery } from "pages/application/applicationService";

interface MenuItem {
  icon: string;
  label: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

type MenuEntry = MenuItem | { isDivider: true; dividerKey: string };

const MY_LOANS_TTL_MS = 5 * 60 * 1000;

const ProfileMenuDropdown: FC = () => {
  const userId = useSelector(getCurrentUserId);
  const displayMyLoans = useSelector(getDisplayMyLoans);
  const myLoansFetchedAt = useSelector(getMyLoansFetchedAt);
  const myLoansStatus = useSelector(getMyLoansStatus);
  const now = Date.now();
  const isStale = !myLoansFetchedAt || now - myLoansFetchedAt > MY_LOANS_TTL_MS;
  const shouldFetchFromQuery =
    Boolean(userId) &&
    (myLoansStatus === "idle" || myLoansStatus === "rejected" || isStale);
  useGetMyLoansQuery(userId, {
    skip: !shouldFetchFromQuery,
    refetchOnMountOrArgChange: true,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const baseMenuItems = useMemo<MenuEntry[]>(() => {
    return [
      {
        icon: settings,
        label: "Profile Settings",
        onClick: () => dispatch(openProfile()),
      },
      { isDivider: true, dividerKey: "profile" },
      {
        icon: logout,
        label: "Logout",
        onClick: async () => {
          await deleteAllFiles();
          dispatch(setLogout());
          navigate("/");
        },
      },
    ];
  }, [dispatch, navigate]);

  const menuItems = useMemo<MenuEntry[]>(() => {
    if (!displayMyLoans) {
      return baseMenuItems;
    }

    return [
      {
        icon: loans,
        label: "My Loans",
        onClick: () => navigate("/my-loans"),
      },
      { isDivider: true, dividerKey: "loans" },
      ...baseMenuItems,
    ];
  }, [baseMenuItems, displayMyLoans, navigate]);

  const isMenuItem = (item: MenuEntry): item is MenuItem => !("isDivider" in item);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>, onClick: MenuItem["onClick"]) => {
    if (event.key === "Enter" || event.key === " ") {
      onClick(event as unknown as MouseEvent<HTMLElement>);
    }
  };

  return (
    <div className="profile-drop-down">
      {menuItems.map((item) => {
        if ("isDivider" in item && item.isDivider) {
          return <div key={`divider-${item.dividerKey}`} className="profile-drop-down__child" />;
        }

        if (!isMenuItem(item)) return null;

        if (item.label === "My Loans") {
          return (
            <NavLink key={item.label} to="/my-loans" state={{ previousPath: location.pathname }} replace className="profile-drop-down__container" end>
              <img className="profile-drop-down__icon" alt={item.label} src={item.icon} />
              <div className="profile-drop-down__label">{item.label}</div>
            </NavLink>
          );
        } else {
          return (
            <button
              type="button"
              key={item.label}
              className={"profile-drop-down__container"}
              onClick={item.onClick}
              tabIndex={0}
              onKeyDown={(e) => handleKeyPress(e, item.onClick)}
            >
              <img className="profile-drop-down__icon" alt={item.label} src={item.icon} />
              <div className="profile-drop-down__label">{item.label}</div>
            </button>
          );
        }
      })}
    </div>
  );
};

export default ProfileMenuDropdown;
